"use client"

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Package, ShoppingCart, BarChart2, Smartphone, Zap, Settings, Lock, X, Wrench, Menu, Truck, BookOpen, HandCoins, Users, Shield, LogOut, Moon, Sun, ClipboardCheck, ScanLine, Check, Hammer, Globe } from 'lucide-react'
import { applyTheme, getCurrentTheme, oppositeTheme, themeFamily, DEFAULT_THEME, type Theme } from '@/lib/theme'
// framer-motion removed 2026-09-06 — see the note in top-header.tsx. This file
// is the other half of app/(app)/layout.tsx, so it was in the cold-start path
// of every logged-in page. All of it maps onto CSS transitions the browser
// already does for free.
import { cn } from '@/lib/utils'
import { selectOpenShift, useStore } from '@/lib/store'
import { APP_VERSION } from '@/lib/app-version'
import { canOpen } from '@/lib/roles'
import { useFeature } from '@/hooks/use-feature'
import { routeFeatureOk, type FeatureKey } from '@/lib/features'
import { createClient } from '@/lib/supabase/client'
import { signOutThisDevice } from '@/lib/sign-out'
import { toast } from 'sonner'
import { BRAND } from '@/lib/brand'
import { lazy, Suspense, useEffect, useState } from 'react'
import { BottomSheet, BottomSheetContent } from '@/components/ui/bottom-sheet'
import { ListGroup, ListRow } from '@/components/ui/list-group'
import { LanguageSwitch } from '@/components/language-switch'
import { LANGS, LANG_NAMES, setLang, useLang, useT, type Key } from '@/lib/i18n'
import { OrderBadge, useOrderAlertBadge } from '@/components/order-alerts'
import { SupportBadge, useSupportAlertBadge } from '@/components/support-alerts'
import { useMediaQuery } from '@/hooks/use-media-query'
import { SIDEBAR_FOLD_QUERY, sidebarFolded } from '@/lib/sidebar-fit'

// glow = the active item's drop-shadow tint ("lamp turning on" — martelounge's
// nm-daylight idea, adapted to our flat/glass style instead of neumorphism).
const NAV_ITEMS: { href: string; icon: React.ElementType; label: Key; color: string; glow: string }[] = [
  { href: '/warehouse', icon: Package,      label: 'nav.warehouse',  color: 'from-blue-500 to-indigo-600',    glow: 'oklch(0.6 0.19 260 / 0.5)' },
  { href: '/pos',       icon: ShoppingCart, label: 'nav.pos',        color: 'from-violet-500 to-purple-600',  glow: 'oklch(0.58 0.22 300 / 0.5)' },
  { href: '/suppliers', icon: Truck,        label: 'nav.suppliers',  color: 'from-cyan-500 to-blue-600',      glow: 'oklch(0.65 0.15 230 / 0.5)' },
  // 130 — shown only where the owner switched repairs on
  { href: '/repairs',   icon: Hammer,       label: 'nav.repairs',    color: 'from-lime-500 to-green-600',     glow: 'oklch(0.72 0.17 135 / 0.5)' },
  // B2 — shown only where the owner switched the online store's orders on
  { href: '/orders',    icon: Globe,        label: 'nav.orders',     color: 'from-sky-500 to-blue-600',       glow: 'oklch(0.66 0.15 240 / 0.5)' },
  { href: '/credit',    icon: HandCoins,    label: 'nav.credit',     color: 'from-amber-500 to-orange-600',   glow: 'oklch(0.7 0.16 55 / 0.5)' },
  { href: '/accounting',icon: BarChart2,    label: 'nav.accounting', color: 'from-emerald-500 to-teal-600',   glow: 'oklch(0.68 0.16 165 / 0.5)' },
  { href: '/staff',     icon: Users,        label: 'nav.staff',      color: 'from-fuchsia-500 to-pink-600',   glow: 'oklch(0.6 0.22 340 / 0.5)' },
  { href: '/tools',     icon: Wrench,       label: 'nav.tools',      color: 'from-orange-500 to-amber-600',   glow: 'oklch(0.7 0.17 45 / 0.5)' },
  { href: '/guide',     icon: BookOpen,     label: 'nav.guide',      color: 'from-pink-500 to-rose-600',      glow: 'oklch(0.62 0.2 8 / 0.5)' },
  { href: '/settings',  icon: Settings,     label: 'nav.settings',   color: 'from-slate-500 to-slate-700',    glow: 'oklch(0.5 0.02 260 / 0.5)' },
]

/**
 * Who is signed in, and whose shift is open — TWO facts, said apart. This used
 * to read „ადმინი" for everybody; then it put the open shift's cashier next to
 * the ACCOUNT's role, so the owner's device read „ჯაბა · მფლობელი" while Jaba
 * was the cashier on shift (the owner, 2026-09-30: „რეალურად ჯაბა მოლარეა და
 * ვისი ცვლაც არის გახსნილი, ის იწერება, ეს შეცდომაა"). The account is known
 * by its role; the shift by its cashier, on its own line.
 * Shared by the desktop sidebar and the phone's „მეტი" sheet.
 */
function useWho() {
  const t = useT()
  const userRole = useStore((s) => s.userRole)
  const openShift = useStore(selectOpenShift)
  const roleLabel = userRole === 'cashier' ? t('role.cashier') : userRole === 'owner' ? t('role.owner') : userRole === 'accountant' ? t('role.accountant') : '—'
  const onShift = openShift?.cashier_name?.trim() || null
  const initials = roleLabel.slice(0, 1).toUpperCase() || '—'
  return { userRole, roleLabel, onShift, initials }
}

/** „ვერსია 1.9" — the release as the owner counts it (lib/app-version.ts); the
 *  minute the bundle was built is its tooltip, for support. */
function useVersion(): { label: string; built: string | undefined } {
  const t = useT()
  const built = process.env.NEXT_PUBLIC_BUILD_AT
  return { label: t('shell.version', { v: APP_VERSION }), built: built ? t('shell.builtAt', { at: built }) : undefined }
}

/**
 * Sign out — the one implementation, used by the desktop sidebar footer and
 * the phone's „მეტი" sheet. `before` closes whatever surface it was pressed on.
 */
function useSignOut() {
  const router = useRouter()
  const t = useT()
  return async (before?: () => void) => {
    before?.()
    const supabase = createClient()
    // Drop the shop from this device BEFORE ending the session. The cache
    // outlives a sign-out, and the next person to use this till is often a
    // different owner — see the note in store-hydrator.tsx.
    useStore.getState().clearTenantCache()
    await signOutThisDevice(supabase)
    toast.success(t('shell.signedOut'))
    router.push('/login')
    router.refresh()
  }
}

function SidebarContent({ onClose, collapsed = false, onCollapse }: {
  onClose?: () => void
  collapsed?: boolean
  /** The ≡ button. On a narrow screen it opens the menu for a moment
   *  (lib/sidebar-fit.ts), on a wide one it is the remembered choice. */
  onCollapse: (collapsed: boolean) => void
}) {
  const t = useT()
  const pathname = usePathname()
  const { lock, settings, isPlatformAdmin } = useStore()
  const { userRole, roleLabel, onShift, initials } = useWho()
  const version = useVersion()
  const signOut = useSignOut()

  // A cashier's menu is their whole app, so it lists only what they may open.
  // Same `canOpen` the route guard uses — the menu and the router cannot
  // disagree about what exists.
  const shown = useMenuFilter(userRole)
  const navItems = NAV_ITEMS.filter((n) => shown(n.href))
  // the online orders waiting for an answer (components/order-alerts.tsx)
  const orders = useOrderAlertBadge()
  // 1.45 — the shops waiting for help, on „პლატფორმა" (components/support-alerts.tsx)
  const support = useSupportAlertBadge()

  const handleLogout = () => signOut(onClose)

  return (
    <aside className={cn(
      "h-full flex flex-col bg-transparent overflow-hidden transition-all duration-300",
      collapsed ? "w-20" : "w-64"
    )}>

      {/* Logo area */}
      <div className={cn("pt-6 pb-5 flex items-center", collapsed ? "px-5 justify-center flex-col gap-4 h-auto" : "px-5 h-20")}>
        <div className={cn("flex items-center w-full", collapsed ? "justify-center" : "gap-3")}>
          <div className="size-10 rounded-2xl bg-gradient-to-br from-primary to-indigo-700 flex items-center justify-center shadow-lg shadow-primary/30 shrink-0">
            <Smartphone className="size-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-foreground tracking-tight leading-none truncate">
                {settings.companyName || BRAND}
              </p>
              <p className="text-[12px] text-muted-foreground mt-0.5">{t('shell.panel')}</p>
            </div>
          )}
          
          {/* Close button — mobile only */}
          {onClose && !collapsed && (
            <button
              onClick={onClose}
              className="md:hidden size-8 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:bg-accent transition-colors shrink-0 ml-auto"
              aria-label={t('shell.menuClose')}
            >
              <X className="size-4" />
            </button>
          )}

          {/* Desktop collapse toggle */}
          {!onClose && !collapsed && (
             <button
                onClick={() => onCollapse(true)}
                className="hidden md:flex size-8 rounded-xl bg-muted/50 hover:bg-muted text-muted-foreground items-center justify-center transition-colors ml-auto shrink-0"
                aria-label={t('shell.menuCollapse')}
             >
                <Menu className="size-4" />
             </button>
          )}
        </div>

        {/* Desktop collapse toggle (Collapsed Mode) */}
        {!onClose && collapsed && (
           <button
              onClick={() => onCollapse(false)}
              className="hidden md:flex size-8 rounded-xl bg-muted/50 hover:bg-muted text-muted-foreground items-center justify-center transition-colors shrink-0"
              aria-label={t('shell.menuExpand')}
           >
              <Menu className="size-4" />
           </button>
        )}
      </div>

      {/* Live badge */}
      {!collapsed && (
        <div className="px-5 mt-2">
          <div className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/25 dark:border-emerald-500/20 rounded-xl px-3 py-2">
            <span className="size-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
            <span className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-400">{t('shell.systemActive')}</span>
            <Zap className="size-3 text-emerald-500 ml-auto" />
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mx-5 my-3" />

      {/* Nav — min-h-0 lets this flex child actually shrink so overflow-y-auto
          can kick in; without it a flex item never scrolls, it just gets
          clipped by the parent's overflow-hidden (this hid Settings/Guide
          on short mobile screens). */}
      {/* Phones hide the scrollbar (the drawer scrolls under a finger; a bar
          there only looks like a desktop page — owner, 2026-09-11). A mouse
          on the desktop still gets one. */}
      <nav className={cn("flex-1 min-h-0 overflow-y-auto px-3 py-2 flex flex-col gap-1 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden", collapsed && "items-center")}>
        {!collapsed && <p className="text-[12px] font-bold text-muted-foreground uppercase tracking-[0.12em] px-3 mb-2">{t('shell.modules')}</p>}

        {navItems.map(({ href, icon: Icon, label, color, glow }) => {
          const active = pathname.startsWith(href)
          const badge = href === '/orders' && orders.count > 0
          return (
            <Link
              key={href}
              href={href}
              onClick={onClose}
              title={collapsed ? t(label) : undefined}
              style={active ? { '--nav-glow': glow } as React.CSSProperties : undefined}
              className={cn(
                // `isolate` = own stacking context, so the coloured pill below
                // (z-0) always paints above the now-opaque sidebar panel and
                // below the label/icon (z-10). Without it the pill sank behind
                // the panel background and left white text on white = invisible.
                // "Daylight" lift: the active item rises 2px with a tinted glow
                // (martelounge's nm-daylight idea, ported to our flat style —
                // transform+shadow only, no blur, off the keyboard-repeat path
                // since this only fires on navigation, not per-keystroke).
                'group relative isolate flex items-center gap-3 py-3 rounded-2xl font-semibold transition-all duration-200',
                collapsed ? 'px-0 justify-center w-12 h-12' : 'px-3 text-sm',
                active
                  ? '-translate-y-0.5 text-white shadow-[0_10px_24px_-6px_var(--nav-glow),0_4px_10px_-2px_var(--nav-glow)]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
            >
              {active && (
                /* layoutId slid this pill between items. Without the library
                   it simply appears on the active one — the colour change is
                   what the eye actually tracks here. */
                <span className={cn('absolute inset-0 rounded-2xl bg-gradient-to-r opacity-100 z-0', color)} />
              )}
              <span className={cn(
                'relative z-10 size-8 rounded-xl flex items-center justify-center shrink-0 transition-all duration-200',
                active ? 'bg-white/20' : 'bg-muted group-hover:bg-white dark:group-hover:bg-white/10 group-hover:shadow-sm'
              )}>
                <Icon className={cn('relative z-10 size-4', active ? 'text-white' : 'text-muted-foreground group-hover:text-primary')} />
                {/* on the icon's corner, as a notification sits — the name keeps its one line */}
                {badge && <OrderBadge count={orders.count} pulse={orders.pulse} className="absolute -right-2 -top-2 z-20" />}
              </span>
              {/* collapsed: the name stays for a screen reader (the badge's words alone would name the link) */}
              <span className={collapsed ? 'sr-only' : 'relative z-10'}>{t(label)}</span>
              {active && !collapsed && (
                <span
                  key={href}
                  className="animate-scale-in relative z-10 ml-auto size-1.5 rounded-full bg-white/90 shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                />
              )}
            </Link>
          )
        })}

        {/* Platform console — only for god-mode admins */}
        {isPlatformAdmin && (
          <Link
            href="/platform"
            onClick={onClose}
            title={collapsed ? t('nav.platform') : undefined}
            className={cn(
              'group relative flex items-center gap-3 py-3 rounded-2xl font-semibold transition-all duration-200 mt-1 border border-indigo-500/20 bg-indigo-500/5 text-indigo-600 hover:bg-indigo-500/10',
              collapsed ? 'px-0 justify-center w-12 h-12' : 'px-3 text-sm',
            )}
          >
            <span className="relative z-10 size-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0 shadow-sm">
              <Shield className="size-4 text-white" />
              <SupportBadge count={support.count} pulse={support.pulse} className="absolute -right-2 -top-2 z-20" />
            </span>
            <span className={collapsed ? 'sr-only' : 'relative z-10'}>{t('nav.platform')}</span>
          </Link>
        )}
      </nav>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mx-5" />

      {/* Footer */}
      <div className={cn("py-4 flex flex-col gap-2", collapsed ? "px-2 items-center" : "px-5")}>
        <LanguageSwitch collapsed={collapsed} />
        {/* Identity row + lock */}
        <div className={cn("flex items-center gap-3", collapsed && "flex-col")}>
          <div className="size-8 rounded-xl bg-gradient-to-br from-primary to-indigo-700 flex items-center justify-center shadow-md shadow-primary/25 shrink-0">
            <span className="text-[12px] font-black text-white">{initials}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-foreground truncate">{roleLabel}</p>
              <p className="text-[12px] text-muted-foreground truncate">{onShift ? t('shell.onShift', { name: onShift }) : t('shell.noShift')}</p>
            </div>
          )}
          <button
            onClick={lock}
            title={t('shell.lockSystem')}
            className={cn("size-8 rounded-xl border border-border hover:bg-red-50 dark:bg-red-500/10 dark:hover:bg-red-500/10 hover:border-red-200 dark:border-red-500/25 dark:hover:border-red-500/30 hover:text-red-500 text-muted-foreground flex items-center justify-center transition-all duration-150 shrink-0", collapsed && "w-10 h-10 size-10")}
          >
            <Lock className="size-3.5" />
          </button>
        </div>

        {/* Sign out — always reachable here (the header logout can clip on phones) */}
        <button
          onClick={handleLogout}
          title={t('shell.signOutTitle')}
          className={cn(
            "rounded-xl border border-border text-muted-foreground hover:bg-red-50 dark:hover:bg-red-500/10 hover:border-red-200 dark:hover:border-red-500/30 hover:text-red-500 flex items-center justify-center gap-2 transition-all duration-150 shrink-0",
            collapsed ? "size-10" : "w-full h-9 px-3 font-semibold text-sm"
          )}
        >
          <LogOut className="size-4 shrink-0" />
          {!collapsed && <span>{t('shell.signOut')}</span>}
        </button>
        {!collapsed && <p className="text-[12px] text-muted-foreground/80 text-center tabular-nums" title={version.built}>{version.label}</p>}
      </div>
    </aside>
  )
}

const StocktakePhone = lazy(() =>
  import('@/components/warehouse/stocktake-phone').then((m) => ({ default: m.StocktakePhone })))

/** The capsule already carries these, so „მეტი" lists everything else. */
const TAB_ROUTES = ['/warehouse', '/pos', '/accounting', '/credit']

/** The app itself rather than the shop — „მეტი"'s second group. */
const APP_ROUTES = ['/settings', '/guide', '/platform']

/** One line under each name in „მეტი": what is inside. The name alone is a
 *  long Georgian word; this is what someone scanning the list actually reads. */
const MORE_DETAIL: Record<string, Key> = {
  '/suppliers': 'more.detail.suppliers',
  '/orders': 'more.detail.orders',
  '/staff': 'more.detail.staff',
  '/tools': 'more.detail.tools',
  '/settings': 'more.detail.settings',
  '/guide': 'more.detail.guide',
  '/platform': 'more.detail.platform',
}

/**
 * The phone's „მეტი" — a bottom sheet, not the old slide-in drawer.
 *
 * Why it had to change, found by the owner on 2026-09-11: the drawer lived
 * inside the shell's `relative z-0` stacking context, so its z-50 counted only
 * INSIDE that context — while the tab capsule and the portaled „დამატება"
 * button sit above the whole context. They covered the drawer's footer, and
 * with it the only sign-out on a phone. The sheet is portaled to <body> and
 * sits above both.
 *
 * The shape is the platform's „More": who you are, then grouped LISTS — the
 * shop's modules, the app (settings, guide, theme), the account (lock, sign
 * out). It was a 3-column grid of tiles first, and the owner's screenshot
 * showed why iOS and Android never do that here: a tile is ~100 px wide and
 * „სახელმძღვანელო" / „თანამშრომლები" do not fit in it. A row gives the name
 * the whole width, room for a line saying what is inside, and one visual
 * language with the rows that were already below it.
 */
/**
 * One rule for both menus (Codex, 153): the role may open the module, and a
 * module that is a feature (ნისია, შეკეთება, ონლაინ შეკვეთები —
 * `FEATURE_ROUTES`) is on for this shop. The phone's „მეტი" used to ask only
 * the first question, so a phone listed what the desktop hid.
 */
function useMenuFilter(userRole: Parameters<typeof canOpen>[1]) {
  const on: Partial<Record<FeatureKey, boolean>> = {
    credit: useFeature('credit'),
    repairs: useFeature('repairs'),
    store: useFeature('store'),
  }
  return (href: string) => canOpen(href, userRole) && routeFeatureOk(href, (k) => on[k] ?? true)
}

function MoreSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useT()
  const lang = useLang()
  const pathname = usePathname()
  const router = useRouter()
  const { lock, settings, isPlatformAdmin } = useStore()
  const { userRole, roleLabel, onShift, initials } = useWho()
  const version = useVersion()
  const signOut = useSignOut()

  const shown = useMenuFilter(userRole)
  const orders = useOrderAlertBadge()
  const support = useSupportAlertBadge()
  const modules = [
    ...NAV_ITEMS.filter((n) => !TAB_ROUTES.includes(n.href) && shown(n.href)),
    ...(isPlatformAdmin ? [{ href: '/platform', icon: Shield, label: 'nav.platform' as Key }] : []),
  ]
  const shopModules = modules.filter((m) => !APP_ROUTES.includes(m.href))
  const appModules = modules.filter((m) => APP_ROUTES.includes(m.href))

  const moduleRow = ({ href, icon, label }: { href: string; icon: React.ElementType; label: Key }) => (
    <ListRow
      key={href}
      icon={icon}
      label={t(label)}
      detail={MORE_DETAIL[href] ? t(MORE_DETAIL[href]) : undefined}
      value={href === '/orders' ? <OrderBadge count={orders.count} pulse={orders.pulse} />
        : href === '/platform' ? <SupportBadge count={support.count} pulse={support.pulse} /> : undefined}
      // Where you are: the icon fills. The page title already says it in words.
      iconClassName={pathname.startsWith(href) ? 'bg-primary text-primary-foreground' : undefined}
      onClick={() => { onOpenChange(false); router.push(href) }}
    />
  )

  // A cashier has no warehouse, so when the owner lets staff count (070) the
  // stocktake opens from here. Lazy: every phone opens this sheet, few count.
  // The owner's own entry stays in the warehouse tools.
  const cashierCounts = userRole === 'cashier' && settings.cashierStocktake === true
  // 1.29: the owner finds the stocktake where the cashier does, too.
  const ownerCounts = userRole === 'owner'
  const [countOpen, setCountOpen] = useState(false)

  // The phone's home for the theme (the header hides its toggle below md so
  // the page title fits). Same `applyTheme` as ThemeToggleCompact; read from
  // <html> each time the sheet opens, since the desktop toggle may have moved it.
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME)
  useEffect(() => {
    if (open) setTheme(getCurrentTheme())
  }, [open])
  const toggleTheme = () => {
    const next = oppositeTheme(theme)
    setTheme(next)
    applyTheme(next)
  }

  return (
    <>
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={t('more.title')}>
        <div className="px-4 pb-5 flex flex-col gap-5 overflow-y-auto overscroll-contain">
          <div className="flex items-center gap-3 px-1">
            <span className="size-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center text-base font-black shrink-0">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="text-base font-bold tracking-[-0.01em] text-foreground truncate">{settings.companyName || BRAND}</p>
              <p className="text-[13px] text-muted-foreground truncate">{onShift ? `${roleLabel} · ${t('shell.onShift', { name: onShift })}` : roleLabel}</p>
              <p className="text-[12px] text-muted-foreground/80 tabular-nums" title={version.built}>{version.label}</p>
            </div>
          </div>

          {(shopModules.length > 0 || cashierCounts || ownerCounts || canOpen('/terminal', userRole)) && (
            <ListGroup>
              {/* A phone-only screen, so it lives here and not in the desktop
                  sidebar: a handheld terminal is an Android phone with a
                  laser, and this is the page shaped for its trigger. */}
              {canOpen('/terminal', userRole) && (
                <ListRow
                  icon={ScanLine}
                  label={t('more.terminal')}
                  detail={t('terminal.detail')}
                  iconClassName={pathname.startsWith('/terminal') ? 'bg-primary text-primary-foreground' : undefined}
                  onClick={() => { onOpenChange(false); router.push('/terminal') }}
                />
              )}
              {cashierCounts && (
                <ListRow
                  icon={ClipboardCheck}
                  label={t('more.stocktake')}
                  detail={t('more.stocktakeDetail')}
                  onClick={() => { onOpenChange(false); requestAnimationFrame(() => setCountOpen(true)) }}
                />
              )}
              {ownerCounts && (
                <ListRow
                  icon={ClipboardCheck}
                  label={t('more.stocktake')}
                  detail={t('more.stocktakeOwnerDetail')}
                  onClick={() => { onOpenChange(false); requestAnimationFrame(() => setCountOpen(true)) }}
                />
              )}
              {shopModules.map(moduleRow)}
            </ListGroup>
          )}

          <ListGroup>
            {appModules.map(moduleRow)}
            <ListRow
              icon={themeFamily(theme) === 'dark' ? Moon : Sun}
              label={t('more.darkMode')}
              value={themeFamily(theme) === 'dark' ? t('common.on') : t('common.off')}
              chevron={false}
              onClick={toggleTheme}
            />
          </ListGroup>

          {/* Each language in its own words — see LanguageSwitch. Rows
              without icons, so the hairline starts at the text. */}
          <ListGroup label={t('common.language')} className="[--row-inset:0.75rem]">
            {LANGS.map((l) => (
              <ListRow
                key={l}
                label={<span lang={l}>{LANG_NAMES[l]}</span>}
                value={lang === l ? <Check className="size-4 text-primary" /> : undefined}
                chevron={false}
                onClick={() => void setLang(l)}
              />
            ))}
          </ListGroup>

          <ListGroup>
            <ListRow
              icon={Lock}
              label={t('more.lock')}
              detail={t('more.lockDetail')}
              chevron={false}
              onClick={() => { onOpenChange(false); lock() }}
            />
            <ListRow
              icon={LogOut}
              label={t('shell.signOut')}
              destructive
              onClick={() => signOut(() => onOpenChange(false))}
            />
          </ListGroup>
        </div>
      </BottomSheetContent>
    </BottomSheet>
    {/* Outside the sheet's Dialog.Root: the stocktake's own sheets must not
        read as dialogs nested in a closed „მეტი". */}
    {countOpen && (
      <Suspense fallback={null}>
        <StocktakePhone open onClose={() => setCountOpen(false)} />
      </Suspense>
    )}
    </>
  )
}

export function Sidebar() {
  const { isMobileSidebarOpen, setMobileSidebarOpen, isDesktopSidebarCollapsed, setDesktopSidebarCollapsed } = useStore()
  const pathname = usePathname()
  // 1.39 — below 1366 px the menu starts folded, and ≡ opens it over the page
  // for a moment (lib/sidebar-fit.ts: a 4:3 till's tiles were 71 px behind it).
  // The peek lives in the store because the till reads it: its hotkeys stand
  // down while the menu covers the page (cart-panel.tsx).
  const narrow = useMediaQuery(SIDEBAR_FOLD_QUERY)
  const peek = useStore((s) => s.sidebarPeek)
  const setPeek = useStore((s) => s.setSidebarPeek)
  const collapsed = sidebarFolded({ narrow, saved: isDesktopSidebarCollapsed, peek })
  const over = narrow && peek
  const onCollapse = (c: boolean) => (narrow ? setPeek(!c) : setDesktopSidebarCollapsed(c))

  // Close on route change: the phones' sheet, and a narrow screen's open menu
  useEffect(() => {
    setMobileSidebarOpen(false)
    setPeek(false)
  }, [pathname, setMobileSidebarOpen, setPeek])

  // a window grown past 1366 px and back starts folded again
  useEffect(() => { if (!narrow) setPeek(false) }, [narrow, setPeek])
  // leaving the shell (a sign-out, the kiosk) must not leave the till's keys off
  useEffect(() => () => setPeek(false), [setPeek])

  useEffect(() => {
    if (!over) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPeek(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [over, setPeek])

  return (
    <>
      {/* Desktop — always visible; width animates via a CSS transition.
          Width is one of the properties that costs a layout pass, but this
          runs once on a click, not per frame of a list.
          The outer box is the room the page leaves; the panel inside it is
          the menu. On a narrow screen the room stays 5rem and the opened
          menu lies over the page (z-40: above the header's z-20), so the
          till's tiles do not jump under it. */}
      <div
        // rem, not px: the sidebar scales with the screen density (lib/density.ts)
        style={{ width: narrow || collapsed ? '5rem' : '16rem' }}
        className="hidden md:block relative shrink-0 h-full transition-[width] duration-300 ease-out"
      >
        <div
          style={{ width: collapsed ? '5rem' : '16rem' }}
          className={cn(
            'absolute inset-y-0 left-0 flex floating-panel rounded-3xl overflow-hidden transition-[width] duration-300 ease-out',
            narrow && 'z-40',
          )}
        >
          <SidebarContent collapsed={collapsed} onCollapse={onCollapse} />
        </div>
      </div>
      {/* the page under an opened narrow menu: a tap folds it back */}
      {over && <div aria-hidden className="hidden md:block fixed inset-0 z-30 bg-black/10" onClick={() => setPeek(false)} />}

      {/* Phones: „მეტი" is a bottom sheet (opened from the tab capsule). */}
      <MoreSheet open={isMobileSidebarOpen} onOpenChange={setMobileSidebarOpen} />
    </>
  )
}
